import { useEffect } from 'react';

/**
 * Hook para mitigar el warning/ERROR de ResizeObserver en Chromium.
 * 1) Intercepta el error en fase de captura para suprimirlo.
 * 2) Parchea ResizeObserver para ejecutar callbacks en rAF y evitar loops.
 */
export const useResizeObserverFix = () => {
  useEffect(() => {
    // 1) Interceptar errores del ResizeObserver en fase de captura
    const handleErrorCapture = (e) => {
      const msg = e?.message || e?.error?.message || '';
      if (
        typeof msg === 'string' && (
          msg.includes('ResizeObserver loop completed with undelivered notifications') ||
          msg.includes('ResizeObserver loop limit exceeded')
        )
      ) {
        e.stopImmediatePropagation?.();
        e.preventDefault?.();
        return false;
      }
    };

    const handleUnhandledRejectionCapture = (e) => {
      const reasonMsg = e?.reason?.message || '';
      if (
        typeof reasonMsg === 'string' && (
          reasonMsg.includes('ResizeObserver loop completed with undelivered notifications') ||
          reasonMsg.includes('ResizeObserver loop limit exceeded')
        )
      ) {
        e.preventDefault?.();
        return false;
      }
    };

    window.addEventListener('error', handleErrorCapture, true);
    window.addEventListener('unhandledrejection', handleUnhandledRejectionCapture, true);

    // 2) Parchear ResizeObserver para ejecutar el callback en rAF y evitar cascadas
    try {
      const w = window;
      if (w && w.ResizeObserver && !w.__patchedResizeObserver) {
        const OriginalRO = w.ResizeObserver;
        class PatchedResizeObserver extends OriginalRO {
          constructor(callback) {
            const wrapped = (entries, observer) => {
              // Deferir al próximo frame para evitar el loop de notificaciones
              requestAnimationFrame(() => {
                try {
                  callback(entries, observer);
                } catch (_) {
                  // Ignorar errores del callback del usuario
                }
              });
            };
            super(wrapped);
          }
        }
        w.ResizeObserver = PatchedResizeObserver;
        w.__patchedResizeObserver = true;
      }
    } catch (_) {
      // No interrumpir la app si no podemos parchear
    }

    return () => {
      window.removeEventListener('error', handleErrorCapture, true);
      window.removeEventListener('unhandledrejection', handleUnhandledRejectionCapture, true);
    };
  }, []);
};

export default useResizeObserverFix;

